import { useEffect, useState } from "react";

import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";

import { Button } from "@mui/material";
import { PageContainer } from "@/features/ui";

import { GoodsReceiptForm } from "@/features/goods-receipts";
import { apiOutletClient, getErrorMessage } from "@/features/shared";
import { CenteredLoading, useSnackbar } from "@/features/ui";

export const EditGoodsReceipt = () => {
  const { referenceNumber } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [defaultValues, setDefaultValues] = useState(null);

  useEffect(() => {
    const fetchGoodsReceipt = async () => {
      setIsLoading(true);
      try {
        const {
          data: { data },
        } = await apiOutletClient.get(`goods-receipts/${referenceNumber}`, {
          params: {
            withBuyer: true,
            withSeller: true,
            withWarehouse: true,
            withProductVariants: true,
            withCreator: true,
          },
        });

        setDefaultValues({
          referenceNumber: data.referenceNumber,
          transactionDate: dayjs(data.transactionDate),
          warehouse: data.warehouse,
          buyer: data.buyer,
          seller: data.seller,
          sellerReferenceNumber: data.sellerReferenceNumber,
          note: data.note,
          goodsReceiptProductVariants: data.goodsReceiptProductVariants.map(
            (variant) => {
              const maxQuantity = variant.purchaseOrderProductVariant
                ? variant.quantity +
                  (variant.purchaseOrderProductVariant.quantity -
                    variant.purchaseOrderProductVariant.receivedQuantity)
                : null;

              return {
                id: variant.id,
                purchaseOrderProductVariantId:
                  variant.purchaseOrderProductVariantId,
                name: variant.name,
                sku: variant.sku,
                unitOfMeasurement: variant.product.unitOfMeasurement.name,
                quantity: variant.quantity,
                note: variant.note,
                invoicedQuantity: variant.invoicedQuantity,
                returnedQuantity: variant.returnedQuantity,
                maxQuantity,
              };
            }
          ),
        });
      } catch (error) {
        const errorMessage =
          getErrorMessage(error, "Gagal memperbarui penerimaan barang");
        showSnackbar(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };
    fetchGoodsReceipt();
  }, [referenceNumber, showSnackbar]);

  const handleSubmit = async (formPayload) => {
    try {
      await apiOutletClient.patch(
        `goods-receipts/${referenceNumber}`,
        formPayload,
        {
          headers: {
            "Outlet-Id": defaultValues.buyer.id,
          },
        }
      );
      showSnackbar("Penerimaan barang berhasil diperbarui", "success");
      navigate(-1);
    } catch (error) {
      const errorMessage =
        getErrorMessage(error, "Gagal memperbarui penerimaan barang");
      showSnackbar(errorMessage);
    }
  };

  if (!defaultValues && isLoading) {
    return <CenteredLoading />;
  }

  return (
    <PageContainer
      maxWidth="xl"
      title="Ubah Penerimaan Barang"
      breadcrumbs={[
        { title: "Penerimaan Barang", path: "/procurement/goods-receipts" },
        {
          title: referenceNumber,
          path: `/procurement/goods-receipts/${referenceNumber}`,
        },
        { title: "Ubah" },
      ]}
    >
      <GoodsReceiptForm
        editMode={true}
        isLoading={isLoading}
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        renderActionButton={({ isSubmitting }) => (
          <Button type="submit" variant="contained" loading={isSubmitting}>
            Simpan
          </Button>
        )}
      />
    </PageContainer>
  );
};
